import { Stack } from '@mui/material';
import { ColDef, GetRowIdParams, GridApi, GridReadyEvent, RowClassParams } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import * as yup from 'yup';
import { ICustomFieldDataDataModel } from '../../../../data-models/custom-field-data.data-model';
import { RendererType } from '../../../../data-models/field-definition';
import { field2ToField3 } from '../../../../data-models/field2.data-model';
import { IField3 } from '../../../../data-models/field3.data-model';
import { useCustomFieldDataActions } from '../../../../services/hooks/useCustomFieldDataActions';
import { customFieldsByIdMapAtom } from '../../../../services/state/AdminPanel/CustomFieldsStateJ';
import { editableGridCss, errorRowClass } from '../../../../theme/gridStyle';
import { AgFormatterService } from '../../../../util/ag-formatter-service';
import {
  getEditableDateFieldColDefs,
  getEditableFieldColDefs,
  withOverrides,
} from '../../../../util/ag-grid-utils';
import { CustomFormatterId } from '../../../../util/formatter-service';
import { DateFormattersId } from '../../../../util/formatters/DateFormatters';
import { useAsync } from '../../../../util/hook-utils';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { captureUnexpectedState } from '../../../../util/SentryUtils';
import { AgTableWithBaseStyle } from '../../../AgTable/AgTable2';
import { DeleteCustomFieldDataRenderer, SaveCustomFieldData } from './TimeSeriesActionComponents';

interface ITimeSeriesDataTableProps {
  entityId: number | null;
  customFieldId: number | null;
}
export function TimeSeriesDataTable({ entityId, customFieldId }: ITimeSeriesDataTableProps) {
  const defs = useCustomFieldDataGridDefs({ entityId, customFieldId });

  return <AgTableWithBaseStyle {...defs} defaultColDef={defaultColDef} />;
}

export function TimeSeriesDataTableEditable({ entityId, customFieldId }: ITimeSeriesDataTableProps) {
  const defs = useCustomFieldDataGridDefs({
    entityId,
    customFieldId,
    editable: true,
  });

  const [api, setApi] = useState<GridApi<CustomFieldDataGridData> | null>(null);
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setApi(params.api);
  }, []);

  return (
    <Stack height='100%' display='grid' gridTemplateRows='1fr auto'>
      <Stack height='calc(100% - 2rem)' css={editableGridCss}>
        <AgTableWithBaseStyle {...defs} defaultColDef={defaultColDef} onGridReady={onGridReady} />
      </Stack>
      {api && <SaveCustomFieldData api={api} />}
    </Stack>
  );
}

function customFieldDataGridFields() {
  return {
    id: yup.number().required(),
    createdAt: yup.date().nullable(),
    createdBy: yup.string().nullable(),
    date: yup.string().required(),
    updatedBy: yup.string().nullable().gridMeta({
      formatter: CustomFormatterId.userByEmail,
    }),
    customFieldId: yup.number().required(),
    entityId: yup.number().required(),
    error: yup.boolean(),
    value: yup.mixed().nullable(),
    startDate: yup.date().required(),
    endDate: yup.date(),
    updatedAt: yup.date().nullable().label('Updated On'),
  };
}
function customFieldDataGridSchema() {
  return yup.object().shape(customFieldDataGridFields());
}

export interface CustomFieldDataGridData extends ICustomFieldDataDataModel {
  startDate: string;
  endDate: string | null | undefined;
  error?: boolean;
}

export function getCustomFieldDataGridData(history?: ICustomFieldDataDataModel[] | null) {
  if (!history) {
    return;
  }
  const sortedHistory = history.toSorted((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const rowData: CustomFieldDataGridData[] = [];
  for (let i = 0; i < sortedHistory.length; i++) {
    const nextDate = i < sortedHistory.length - 1 ? sortedHistory[i + 1].date : null;
    const endDate = nextDate ? Temporal.PlainDate.from(nextDate).subtract({ days: 1 }) : null;
    const row = {
      ...sortedHistory[i],
      startDate: sortedHistory[i].date,
      endDate: endDate?.toString(),
      updatedBy: sortedHistory[i].updatedBy || sortedHistory[i].createdBy,
      updatedAt: sortedHistory[i].updatedAt || sortedHistory[i].createdAt,
    };
    rowData.push(row);
  }
  return rowData;
}

function useCustomFieldDataGridDefs({
  entityId,
  customFieldId,
  editable,
}: ITimeSeriesDataTableProps & { editable?: boolean }): AgGridReactProps {
  const schemaToGrid = useSchemaToGrid();
  const { fetchCustomFieldDataHistory } = useCustomFieldDataActions();
  const { data } = useAsync(
    useCallback(async () => {
      if (!entityId || !customFieldId) {
        captureUnexpectedState('Missing entityId or customFieldId', { entityId, customFieldId });
        return;
      }
      return fetchCustomFieldDataHistory({ entityId, customFieldId });
    }, [entityId, customFieldId, fetchCustomFieldDataHistory]),
    {
      id: `${entityId}-${customFieldId}`,
    }
  );

  const field = useAtomValue(customFieldsByIdMapAtom)?.get(customFieldId ?? -1);

  return useMemo(() => {
    if (!field) {
      captureUnexpectedState('Missing field', { customFieldId });
      return {};
    }
    const rowData = getCustomFieldDataGridData(data);

    const fieldPaths = ['value', 'startDate', 'updatedAt', 'updatedBy'] as (keyof CustomFieldDataGridData)[];
    if (!editable) {
      fieldPaths.splice(2, 0, 'endDate');
    }

    const startDateOverrides = { sort: 'desc' as const };
    const overrides: Partial<Record<keyof CustomFieldDataGridData, ColDef<CustomFieldDataGridData>>> = {
      startDate: editable
        ? { ...getEditableDateFieldColDefs('startDate'), ...startDateOverrides }
        : startDateOverrides,
      value: getValueFieldOverrides(field2ToField3(field), editable),
      updatedAt: { valueFormatter: AgFormatterService.get().getFormatterForId(DateFormattersId.dateLocal) },
    };

    return {
      columnDefs: withOverrides(
        schemaToGrid(customFieldDataGridSchema().pick(fieldPaths)).concat(getAdditionalColDefs(editable)),
        overrides
      ),
      getRowId: (params: GetRowIdParams<CustomFieldDataGridData>) => `${params.data?.id}`,
      getRowClass,
      rowData,
      suppressLastEmptyLineOnPaste: true,
    };
  }, [customFieldId, data, editable, field, schemaToGrid]);
}

const defaultColDef: ColDef = {
  flex: 1,
  minWidth: 100,
  resizable: true,
};

function getAdditionalColDefs(editable?: boolean): ColDef[] {
  return editable
    ? [
        {
          headerName: '',
          cellRenderer: DeleteCustomFieldDataRenderer,
          pinned: 'right',
          maxWidth: 70,
          suppressHeaderMenuButton: true,
        },
      ]
    : [];
}

function getRowClass(params: RowClassParams) {
  if (params.data?.error) {
    return errorRowClass;
  }
  return undefined;
}

function getValueFieldOverrides(field: IField3<unknown>, editable?: boolean): ColDef {
  // value can be any type, so we need to use the field to get the formatter (other defs should come from gridToSchema,
  // as they are based on the CustomFieldData structure)
  if (!editable) {
    return {
      filter: true,
      valueFormatter: AgFormatterService.get().getFormatterForFieldV3(field),
    };
  }
  if (field.dataType === RendererType.date) {
    return {
      ...getEditableDateFieldColDefs('value'),
      filter: true,
    };
  }
  return {
    ...getEditableFieldColDefs(field, 'value'),
    valueFormatter: AgFormatterService.get().getFormatterForFieldV3(field),
    filter: true,
  } as ColDef;
}
